import React, { useState, useEffect } from 'react';
import axios from '../utils/api';
import ContributionHeatmap from '../components/ContributionHeatmap';
import { GitCommit, GitPullRequest, MessageSquare, Calendar, ExternalLink, Trophy } from 'lucide-react';

const Contributions = () => {
  const [contributions, setContributions] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    byType: {}
  });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    let cancelled = false

    const fetchContributions = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('/user/contributions', {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (cancelled) return
        setContributions(response.data);
        calculateStats(response.data);
        setLoading(false);
      } catch (error) {
        if (cancelled) return
        console.error('Error fetching contributions:', error);
        setLoading(false);
      }
    };

    fetchContributions()
    return () => { cancelled = true }
  }, []);

  const calculateStats = (contribs) => {
    const byType = {};
    contribs.forEach(c => {
      byType[c.contributionType] = (byType[c.contributionType] || 0) + 1;
    });

    setStats({
      total: contribs.length,
      byType
    });
  };

  const getContributionIcon = (type) => {
    switch (type) {
      case 'commit':
        return <GitCommit className="w-5 h-5" />;
      case 'pull_request':
        return <GitPullRequest className="w-5 h-5" />;
      case 'issue':
        return <MessageSquare className="w-5 h-5" />;
      default:
        return <GitCommit className="w-5 h-5" />;
    }
  };

  const getContributionColor = (type) => {
    switch (type) {
      case 'commit':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'pull_request':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'issue':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const filteredContributions = filter === 'all' 
    ? contributions 
    : contributions.filter(c => c.contributionType === filter);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading contributions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Trophy className="w-8 h-8 text-primary" />
            <h1 className="text-3xl font-bold text-foreground">My Contributions</h1>
          </div>
          <p className="text-muted-foreground">Your contribution history across all open repositories</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-card rounded-lg shadow-sm p-6 border-l-4 border-primary">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Total Contributions</p>
                <p className="text-3xl font-bold text-card-foreground">{stats.total}</p>
              </div>
              <Trophy className="w-10 h-10 text-primary opacity-50" />
            </div>
          </div>

          <div className="bg-card rounded-lg shadow-sm p-6 border-l-4 border-primary">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Commits</p>
                <p className="text-3xl font-bold text-card-foreground">{stats.byType.commit || 0}</p>
              </div>
              <GitCommit className="w-10 h-10 text-primary opacity-50" />
            </div>
          </div>

          <div className="bg-card rounded-lg shadow-sm p-6 border-l-4 border-primary">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Pull Requests</p>
                <p className="text-3xl font-bold text-card-foreground">{stats.byType.pull_request || 0}</p>
              </div>
              <GitPullRequest className="w-10 h-10 text-primary opacity-50" />
            </div>
          </div>

          <div className="bg-card rounded-lg shadow-sm p-6 border-l-4 border-primary">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Issues</p>
                <p className="text-3xl font-bold text-card-foreground">{stats.byType.issue || 0}</p>
              </div>
              <MessageSquare className="w-10 h-10 text-primary opacity-50" />
            </div>
          </div>
        </div>

        {/* Contribution Heatmap */}
        {contributions.length > 0 && (
          <div className="bg-card rounded-lg shadow-sm p-6 mb-8">
            <ContributionHeatmap 
              commits={contributions.map(c => ({
                date: c.contributedAt,
                commit: { author: { date: c.contributedAt } }
              }))} 
              title="Your Overall Contribution Activity" 
            />
          </div>
        )}

        {/* Filters */}
        <div className="bg-card rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'all'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
              }`}
            >
              All ({stats.total})
            </button>
            <button
              onClick={() => setFilter('commit')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'commit'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
              }`}
            >
              Commits ({stats.byType.commit || 0})
            </button>
            <button
              onClick={() => setFilter('pull_request')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'pull_request'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
              }`}
            >
              Pull Requests ({stats.byType.pull_request || 0})
            </button>
            <button
              onClick={() => setFilter('issue')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'issue'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
              }`}
            >
              Issues ({stats.byType.issue || 0})
            </button>
          </div>
        </div>

        {/* Contributions List */}
        {filteredContributions.length > 0 ? (
          <div className="space-y-4">
            {filteredContributions.map((contribution) => (
              <div
                key={contribution.id}
                className="bg-card rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-lg border ${getContributionColor(contribution.contributionType)}`}>
                    {getContributionIcon(contribution.contributionType)}
                  </div>

                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="text-lg font-semibold text-card-foreground mb-1">
                          {contribution.title}
                        </h3>
                        {contribution.repo && (
                          <p className="text-sm text-muted-foreground mb-2">
                            Repository: <span className="font-medium">{contribution.repo.name}</span>
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="w-4 h-4" />
                        {formatDate(contribution.contributedAt)}
                      </div>
                    </div>

                    {contribution.description && (
                      <p className="text-muted-foreground mb-3">{contribution.description}</p>
                    )}

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <span className="text-sm font-medium text-primary">
                          +{contribution.pointsEarned} points
                        </span>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getContributionColor(contribution.contributionType)}`}>
                          {contribution.contributionType.replace('_', ' ')}
                        </span>
                      </div>

                      {contribution.githubUrl && (
                        <a
                          href={contribution.githubUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-primary hover:opacity-80 font-medium text-sm transition-colors"
                        >
                          View on GitHub
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-card rounded-lg shadow-sm">
            <Trophy className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-card-foreground mb-2">No contributions yet</h3>
            <p className="text-muted-foreground">Start contributing to open repositories to see your contributions here!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Contributions;
